import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as onboardingApi from '../services/onboardingApi';
import { flattenStructuredTasks, REQUEST_TYPES } from '../components/onboarding/constants';

// Mute en place l'objet structuredTasks (même logique que le backend) pour un update optimiste.
const ensureStructuredTasks = (progress) => {
  if (!progress.structuredTasks) progress.structuredTasks = { globalTasks: [], tasksByStructure: [] };
  if (!Array.isArray(progress.structuredTasks.globalTasks)) progress.structuredTasks.globalTasks = [];
  if (!Array.isArray(progress.structuredTasks.tasksByStructure)) progress.structuredTasks.tasksByStructure = [];
  return progress.structuredTasks;
};

const recomputeProgress = (st) => {
  const all = [...(st.globalTasks || []), ...(st.tasksByStructure || []).flatMap((sg) => sg.tasks || [])];
  st.overallProgress = all.length ? Math.round((all.filter((t) => t.status === 'done').length / all.length) * 100) : 0;
};

// Async thunks
export const listOnboardings = createAsyncThunk(
  'atelier/list',
  async (filters, { rejectWithValue }) => {
    try { return await onboardingApi.adminListOnboardings(filters); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const fetchOnboardingForAdmin = createAsyncThunk(
  'atelier/fetch',
  async (onboardingId, { rejectWithValue }) => {
    try { return await onboardingApi.adminFetchOnboarding(onboardingId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const patchWorkshop = createAsyncThunk(
  'atelier/patchWorkshop',
  async ({ onboardingId, section, data }, { rejectWithValue }) => {
    try { return await onboardingApi.adminUpdateWorkshop(onboardingId, section, data); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const patchVirageNumeriqueDiscount = createAsyncThunk(
  'atelier/patchVirageNumeriqueDiscount',
  async ({ onboardingId, enabled }, { rejectWithValue }) => {
    try { return await onboardingApi.adminUpdateVirageNumeriqueDiscount(onboardingId, enabled); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const patchQualification = createAsyncThunk(
  'atelier/patchQualification',
  async ({ onboardingId, updates, icp }, { rejectWithValue }) => {
    try { return await onboardingApi.adminUpdateQualification(onboardingId, updates, icp); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const patchSketch = createAsyncThunk(
  'atelier/patchSketch',
  async ({ onboardingId, section, data }, { rejectWithValue }) => {
    try { return await onboardingApi.adminUpdateSketch(onboardingId, section, data); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const addWorkshopNote = createAsyncThunk(
  'atelier/addNote',
  async ({ onboardingId, note }, { rejectWithValue }) => {
    try { return await onboardingApi.adminWorkshopNote(onboardingId, 'add', note); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const updateWorkshopNote = createAsyncThunk(
  'atelier/updateNote',
  async ({ onboardingId, noteId, note }, { rejectWithValue }) => {
    try { return await onboardingApi.adminWorkshopNote(onboardingId, 'update', note, noteId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const deleteWorkshopNote = createAsyncThunk(
  'atelier/deleteNote',
  async ({ onboardingId, noteId }, { rejectWithValue }) => {
    try { return await onboardingApi.adminWorkshopNote(onboardingId, 'delete', null, noteId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const analyzeDiscovery = createAsyncThunk(
  'atelier/analyzeDiscovery',
  async ({ onboardingId, sourceText }, { rejectWithValue }) => {
    try { return await onboardingApi.adminAnalyzeDiscovery(onboardingId, sourceText); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

// ---- Demandes client (progress.structuredTasks) ----
export const addOnboardingTask = createAsyncThunk(
  'atelier/addOnboardingTask',
  async ({ onboardingId, task, structureId = null, structureName = null }, { rejectWithValue }) => {
    try { return await onboardingApi.adminAddOnboardingTask(onboardingId, task, structureId, structureName); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const updateOnboardingTask = createAsyncThunk(
  'atelier/updateOnboardingTask',
  async ({ onboardingId, taskId, updates }, { rejectWithValue }) => {
    try { return await onboardingApi.adminUpdateOnboardingTask(onboardingId, taskId, updates); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const deleteOnboardingTask = createAsyncThunk(
  'atelier/deleteOnboardingTask',
  async ({ onboardingId, taskId }, { rejectWithValue }) => {
    try { return await onboardingApi.adminDeleteOnboardingTask(onboardingId, taskId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

// Rafraîchit uniquement progress (réponses/fichiers client) sans réinitialiser l'étape active.
export const refreshOnboardingProgress = createAsyncThunk(
  'atelier/refreshProgress',
  async (onboardingId, { rejectWithValue }) => {
    try { return await onboardingApi.adminFetchOnboarding(onboardingId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const seedFromAnswers = createAsyncThunk(
  'atelier/seed',
  async (onboardingId, { rejectWithValue }) => {
    try { return await onboardingApi.adminSeedFromAnswers(onboardingId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const generateBlueprint = createAsyncThunk(
  'atelier/generateBlueprint',
  async ({ onboardingId, userId }, { rejectWithValue }) => {
    try { return await onboardingApi.adminGenerateBlueprint(onboardingId, userId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const validateConversion = createAsyncThunk(
  'atelier/validateConversion',
  async (onboardingId, { rejectWithValue }) => {
    try { return await onboardingApi.adminValidateConversion(onboardingId); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

export const runConversion = createAsyncThunk(
  'atelier/runConversion',
  async ({ onboardingId, dryRun, plan }, { rejectWithValue }) => {
    try { return await onboardingApi.adminRunConversion(onboardingId, dryRun, plan); }
    catch (error) { return rejectWithValue({ message: error.message }); }
  }
);

const initialState = {
  onboardings: [],
  listLoading: false,
  listError: null,

  current: null,
  detailLoading: false,
  detailError: null,
  currentRequestId: null,

  workshopSaving: false,
  sketchSaving: false,
  blueprintGenerating: false,
  discoveryAnalyzing: false,

  conversionValidation: null,
  conversionResult: null,
  conversionRunning: false,

  activeStep: 0,
};

const atelierSlice = createSlice({
  name: 'atelier',
  initialState,
  reducers: {
    setActiveStep: (state, action) => { state.activeStep = action.payload; },
    clearCurrent: (state) => {
      state.current = null;
      state.detailError = null;
      state.currentRequestId = null;
      state.activeStep = 0;
    },
    clearConversion: (state) => { state.conversionValidation = null; state.conversionResult = null; },
    updateCurrentWorkshop: (state, action) => {
      if (state.current) {
        state.current.workshop = { ...(state.current.workshop || {}), ...action.payload };
      }
    },
    updateCurrentSketch: (state, action) => {
      if (state.current) {
        state.current.sketch = { ...(state.current.sketch || {}), ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(listOnboardings.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(listOnboardings.fulfilled, (state, action) => {
        state.listLoading = false;
        state.onboardings = action.payload?.onboardings || action.payload || [];
      })
      .addCase(listOnboardings.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      })

      // Fetch detail
      .addCase(fetchOnboardingForAdmin.pending, (state, action) => {
        state.detailLoading = true;
        state.detailError = null;
        state.current = null;
        state.currentRequestId = action.meta.requestId;
        state.activeStep = 0;
      })
      .addCase(fetchOnboardingForAdmin.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.detailLoading = false;
        state.current = action.payload;
        state.currentRequestId = null;
      })
      .addCase(fetchOnboardingForAdmin.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.detailLoading = false;
        state.current = null;
        state.detailError = action.payload;
        state.currentRequestId = null;
      })

      // Patch workshop — optimistic update from meta.arg
      .addCase(patchWorkshop.pending, (state, action) => {
        state.workshopSaving = true;
        const { section, data } = action.meta.arg;
        if (state.current && section && data !== undefined) {
          state.current.workshop = {
            ...(state.current.workshop || {}),
            [section]: data,
          };
        }
      })
      .addCase(patchWorkshop.fulfilled, (state) => { state.workshopSaving = false; })
      .addCase(patchWorkshop.rejected, (state) => { state.workshopSaving = false; })

      .addCase(patchVirageNumeriqueDiscount.pending, (state, action) => {
        const { enabled } = action.meta.arg;
        if (state.current) {
          state.current.pricing = {
            ...(state.current.pricing || {}),
            virageNumeriqueEnabled: enabled,
          };
        }
      })
      .addCase(patchVirageNumeriqueDiscount.fulfilled, (state, action) => {
        if (state.current && action.payload) {
          if (action.payload.pricing) {
            state.current.pricing = action.payload.pricing;
          }
          if (action.payload.signature) {
            state.current.signature = action.payload.signature;
          }
        }
      })
      .addCase(patchVirageNumeriqueDiscount.rejected, () => {})

      // Patch qualification — optimistic update
      .addCase(patchQualification.pending, (state, action) => {
        const { updates, icp } = action.meta.arg;
        if (state.current && updates) {
          state.current.qualification = {
            ...(state.current.qualification || {}),
            ...updates,
            ...(icp ? { icp } : {}),
          };
        }
      })
      .addCase(patchQualification.fulfilled, () => {})
      .addCase(patchQualification.rejected, () => {})

      // Patch sketch — optimistic update from meta.arg
      .addCase(patchSketch.pending, (state, action) => {
        state.sketchSaving = true;
        const { section, data } = action.meta.arg;
        if (state.current && section && data !== undefined) {
          state.current.sketch = {
            ...(state.current.sketch || {}),
            [section]: data,
          };
        }
      })
      .addCase(patchSketch.fulfilled, (state) => { state.sketchSaving = false; })
      .addCase(patchSketch.rejected, (state) => { state.sketchSaving = false; })

      // Notes
      .addCase(addWorkshopNote.pending, (state) => { state.workshopSaving = true; })
      .addCase(addWorkshopNote.fulfilled, (state, action) => {
        state.workshopSaving = false;
        if (state.current && action.payload) {
          state.current.workshop = { ...(state.current.workshop || {}), ...action.payload };
        }
      })
      .addCase(addWorkshopNote.rejected, (state) => { state.workshopSaving = false; })

      .addCase(updateWorkshopNote.pending, (state) => { state.workshopSaving = true; })
      .addCase(updateWorkshopNote.fulfilled, (state, action) => {
        state.workshopSaving = false;
        if (state.current && action.payload) {
          state.current.workshop = { ...(state.current.workshop || {}), ...action.payload };
        }
      })
      .addCase(updateWorkshopNote.rejected, (state) => { state.workshopSaving = false; })

      .addCase(deleteWorkshopNote.pending, (state) => { state.workshopSaving = true; })
      .addCase(deleteWorkshopNote.fulfilled, (state, action) => {
        state.workshopSaving = false;
        if (state.current && action.payload) {
          state.current.workshop = { ...(state.current.workshop || {}), ...action.payload };
        }
      })
      .addCase(deleteWorkshopNote.rejected, (state) => { state.workshopSaving = false; })

      // Seed from answers
      .addCase(seedFromAnswers.pending, (state) => { state.sketchSaving = true; })
      .addCase(seedFromAnswers.fulfilled, (state, action) => {
        state.sketchSaving = false;
        if (state.current && action.payload) {
          state.current.workshop = {
            ...(state.current.workshop || {}),
            preparation: { ...(state.current.workshop?.preparation || {}), ...action.payload },
          };
        }
      })
      .addCase(seedFromAnswers.rejected, (state) => { state.sketchSaving = false; })

      // Analyze discovery (AI) — read-only, returns proposals (no Firestore write)
      .addCase(analyzeDiscovery.pending, (state) => { state.discoveryAnalyzing = true; })
      .addCase(analyzeDiscovery.fulfilled, (state) => { state.discoveryAnalyzing = false; })
      .addCase(analyzeDiscovery.rejected, (state) => { state.discoveryAnalyzing = false; })

      // Demandes client — update optimiste de progress.structuredTasks (préserve l'étape active)
      .addCase(addOnboardingTask.fulfilled, (state, action) => {
        if (!state.current) return;
        if (!state.current.progress) state.current.progress = {};
        const st = ensureStructuredTasks(state.current.progress);
        const { task, structureId, structureName } = action.meta.arg;
        const newTask = action.payload?.task || { ...task, status: task?.status || 'pending' };
        if (structureId) {
          let group = st.tasksByStructure.find((sg) => sg.structureId === structureId);
          if (!group) { group = { structureId, structureName: structureName || 'Structure', tasks: [] }; st.tasksByStructure.push(group); }
          group.tasks.push(newTask);
        } else {
          st.globalTasks.push(newTask);
        }
        recomputeProgress(st);
        state.current.progress.overallProgress = st.overallProgress;
      })
      .addCase(updateOnboardingTask.fulfilled, (state, action) => {
        if (!state.current?.progress?.structuredTasks) return;
        const st = state.current.progress.structuredTasks;
        const { taskId, updates } = action.meta.arg;
        const apply = (tasks = []) => tasks.forEach((t, i) => { if (t.id === taskId) tasks[i] = { ...t, ...updates }; });
        apply(st.globalTasks);
        (st.tasksByStructure || []).forEach((sg) => apply(sg.tasks));
        recomputeProgress(st);
        state.current.progress.overallProgress = st.overallProgress;
      })
      .addCase(deleteOnboardingTask.fulfilled, (state, action) => {
        if (!state.current?.progress?.structuredTasks) return;
        const st = state.current.progress.structuredTasks;
        const { taskId } = action.meta.arg;
        st.globalTasks = (st.globalTasks || []).filter((t) => t.id !== taskId);
        (st.tasksByStructure || []).forEach((sg) => { sg.tasks = (sg.tasks || []).filter((t) => t.id !== taskId); });
        recomputeProgress(st);
        state.current.progress.overallProgress = st.overallProgress;
      })
      .addCase(refreshOnboardingProgress.fulfilled, (state, action) => {
        if (state.current && action.payload?.progress) {
          state.current.progress = action.payload.progress;
        }
      })

      // Generate blueprint
      .addCase(generateBlueprint.pending, (state) => { state.blueprintGenerating = true; })
      .addCase(generateBlueprint.fulfilled, (state, action) => {
        state.blueprintGenerating = false;
        if (state.current && action.payload) {
          state.current.blueprint = { ...(state.current.blueprint || {}), ...action.payload };
        }
      })
      .addCase(generateBlueprint.rejected, (state) => { state.blueprintGenerating = false; })

      // Validate conversion
      .addCase(validateConversion.pending, (state) => { state.conversionRunning = true; })
      .addCase(validateConversion.fulfilled, (state, action) => {
        state.conversionRunning = false;
        state.conversionValidation = action.payload;
      })
      .addCase(validateConversion.rejected, (state) => { state.conversionRunning = false; })

      // Run conversion
      .addCase(runConversion.pending, (state) => { state.conversionRunning = true; })
      .addCase(runConversion.fulfilled, (state, action) => {
        state.conversionRunning = false;
        state.conversionResult = action.payload;
      })
      .addCase(runConversion.rejected, (state) => { state.conversionRunning = false; });
  },
});

export const {
  setActiveStep,
  clearCurrent,
  clearConversion,
  updateCurrentWorkshop,
  updateCurrentSketch,
} = atelierSlice.actions;

// Selectors
export const selectOnboardings = (state) => state.atelier.onboardings;
export const selectListLoading = (state) => state.atelier.listLoading;
export const selectCurrent = (state) => state.atelier.current;
export const selectDetailLoading = (state) => state.atelier.detailLoading;
export const selectDetailError = (state) => state.atelier.detailError;
export const selectActiveStep = (state) => state.atelier.activeStep;
export const selectWorkshopSaving = (state) => state.atelier.workshopSaving;
export const selectSketchSaving = (state) => state.atelier.sketchSaving;
export const selectBlueprintGenerating = (state) => state.atelier.blueprintGenerating;
export const selectDiscoveryAnalyzing = (state) => state.atelier.discoveryAnalyzing;
export const selectSources = (state) => state.atelier.current?.workshop?.sources || [];
export const selectFindings = (state) => state.atelier.current?.workshop?.findings || [];
export const selectFindingsBySource = (sourceId) => (state) =>
  (state.atelier.current?.workshop?.findings || []).filter((f) => f.sourceId === sourceId);
export const selectFindingsByStatus = (status) => (state) =>
  (state.atelier.current?.workshop?.findings || []).filter((f) => f.status === status);
export const selectFindingsByFeasibility = (feasibility) => (state) =>
  (state.atelier.current?.workshop?.findings || []).filter((f) => f.feasibility === feasibility);
// Demandes client (structuredTasks) aplaties + filtres par espace (group)
export const selectStructuredTasks = (state) => flattenStructuredTasks(state.atelier.current?.progress);
export const selectStructuredTasksByGroup = (group) => (state) =>
  flattenStructuredTasks(state.atelier.current?.progress).filter((t) => (REQUEST_TYPES[t.type]?.group || 'project') === group);
export const selectConversionValidation = (state) => state.atelier.conversionValidation;
export const selectConversionResult = (state) => state.atelier.conversionResult;
export const selectConversionRunning = (state) => state.atelier.conversionRunning;
export const selectWorkshopAgentConversationId = (state) =>
  state.atelier.current?.workshop?.agent?.conversationId || null;

export default atelierSlice.reducer;
